<template>
  <nav 
    ref="barRef"
    class="qp-mb" 
    aria-label="Bottom navigation"
    @click="handleBottomBarClick"
  >
    <router-link to="/" class="qp-mb__tab" :class="{ 'qp-mb__tab--active': $route.path === '/' }">
      <span class="qp-mb__tab-glyph" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18">
          <path d="M2.5 8L9 2.5 15.5 8v7a1 1 0 01-1 1h-3v-5H7v5H3.5a1 1 0 01-1-1V8z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
        </svg>
      </span>
      <span class="qp-mb__tab-label">{{ $t('navbar.home') }}</span>
    </router-link>

    <router-link
      v-if="authState.isAuthenticated"
      to="/smart_search"
      class="qp-mb__tab"
      :class="{ 'qp-mb__tab--active': $route.path.startsWith('/smart_search') }"
    >
      <span class="qp-mb__tab-glyph" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18">
          <circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" stroke-width="1.3"/>
          <path d="M12 12l3 3" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="qp-mb__tab-label">{{ $t('navbar.product_info') }}</span>
    </router-link>

    <button
      type="button"
      class="qp-mb__tab qp-mb__tab--button"
      :class="{ 'qp-mb__tab--active': isMoreOpen }"
      data-more-toggle
      :aria-expanded="isMoreOpen ? 'true' : 'false'"
      @click="toggleMore"
    >
      <span class="qp-mb__tab-glyph" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18">
          <rect x="2" y="2" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.3"/>
          <rect x="10" y="2" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.3"/>
          <rect x="2" y="10" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.3"/>
          <rect x="10" y="10" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.3"/>
        </svg>
      </span>
      <span class="qp-mb__tab-label">{{ $t('navbar.more_short') }}</span>
    </button>

    <router-link
      v-if="authState.isAuthenticated"
      to="/profile"
      class="qp-mb__tab"
      :class="{ 'qp-mb__tab--active': $route.path.startsWith('/profile') }"
    >
      <span class="qp-mb__tab-glyph" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18">
          <circle cx="9" cy="6" r="3" fill="none" stroke="currentColor" stroke-width="1.3"/>
          <path d="M3 15c1-3 3.5-5 6-5s5 2 6 5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="qp-mb__tab-label">{{ $t('navbar.profile') }}</span>
    </router-link>
    <button
      v-else
      type="button"
      class="qp-mb__tab qp-mb__tab--button"
      @click="handleLogin"
    >
      <span class="qp-mb__tab-glyph" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18">
          <path d="M10 2h4a1 1 0 011 1v12a1 1 0 01-1 1h-4M10 9H2M5 6l-3 3 3 3" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <span class="qp-mb__tab-label">{{ $t('navbar.login') }}</span>
    </button>

    <transition name="qp-mb-sheet">
      <div v-show="isMoreOpen" class="qp-mb__sheet" @click.self="closeMore">
        <div class="qp-mb__sheet-panel">

          <router-link to="/enstru-search" class="qp-mb__sheet-link">
            <span class="qp-mb__sheet-link-label">{{ $t('navbar.enstru_search') }}</span>
            <span class="qp-mb__sheet-link-hint">ЕНС-ТРУ</span>
          </router-link>
          <router-link
            v-if="authState.isAuthenticated && accessState.can('nomenclature.view')"
            to="/my-standards/distribution"
            class="qp-mb__sheet-link"
          >
            <span class="qp-mb__sheet-link-label">{{ $t('navbar.my_nomenclature') }}</span>
          </router-link>
          <router-link
            to="/suppliers"
            class="qp-mb__sheet-link"
          >
            <span class="qp-mb__sheet-link-label">{{ $t('navbar.suppliers_directory') }}</span>
          </router-link>
          <router-link to="/unreliable-suppliers" class="qp-mb__sheet-link">
            <span class="qp-mb__sheet-link-label">{{ $t('navbar.unreliable_suppliers') }}</span>
            <span class="qp-mb__sheet-link-hint">РНУ</span>
          </router-link>
          <router-link
            v-permission="'domestic_producer_view'"
            to="/domestic-producers"
            class="qp-mb__sheet-link"
          >
            <span class="qp-mb__sheet-link-label">{{ $t('navbar.domestic_producers') }}</span>
          </router-link>
          <router-link to="/about" class="qp-mb__sheet-link">
            <span class="qp-mb__sheet-link-label">{{ $t('navbar.about') }}</span>
          </router-link>
          <router-link to="/pricing" class="qp-mb__sheet-link">
            <span class="qp-mb__sheet-link-label">{{ $t('navbar.pricing') }}</span>
          </router-link>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { authState } from '@/store/auth';
import { accessState } from '@/store/access';
import { vPermission } from '@/directives/permission';

export default {
  name: 'MobileBottomBar',
  directives: { permission: vPermission },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const isMoreOpen = ref(false);
    const barRef = ref(null);

    const closeMore = () => {
      isMoreOpen.value = false;
    };

    const toggleMore = () => {
      isMoreOpen.value = !isMoreOpen.value;
    };

    const handleBottomBarClick = event => {
      const target = event.target;

      if (target.closest('[data-more-toggle]')) return;
      if (target.closest('a, button')) {
        closeMore();
      }
    };

    const handleDocumentClick = event => {
      if (!isMoreOpen.value) return;

      const target = event.target;
      if (barRef.value?.contains(target)) return;
      closeMore();
    };

    const handleLogin = () => {
      closeMore();
      router.push('/login');
    };

    watch(() => route.fullPath, () => {
        closeMore();
      }
    );

    onMounted(() => {
      document.addEventListener('click', handleDocumentClick);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleDocumentClick);
    });

    return {
      authState,
      accessState,
      isMoreOpen,
      barRef,
      closeMore,
      toggleMore,
      handleBottomBarClick,
      handleLogin,
    };
  },
};
</script>

<style scoped>
.qp-mb {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 64px;
  display: none;
  align-items: stretch;
  padding: var(--space-2xs) var(--space-xs);
  gap: 2px;
  background: var(--surface-base);
  border-top: 1px solid var(--hairline);
  z-index: calc(var(--z-nav) - 1);
}

.qp-mb__tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  font-family: inherit;
  font-size: var(--text-3xs);
  font-weight: var(--weight-medium);
  letter-spacing: var(--tracking-wide);
  color: var(--ink-muted);
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-decoration: none;
  transition:
    color var(--dur-xs) var(--ease-out-quart),
    background-color var(--dur-xs) var(--ease-out-quart);
}

.qp-mb__tab:hover,
.qp-mb__tab:focus-visible {
  outline: 0;
  color: var(--ink);
  background: var(--surface-sunken);
}

.qp-mb__tab--active {
  color: var(--accent);
  background: var(--accent-tint);
}

.qp-mb__tab-glyph {
  display: inline-flex;
  line-height: 0;
}

.qp-mb__tab-label {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Sheet */

.qp-mb__sheet {
  position: fixed;
  inset: 0 0 64px 0;
  background: var(--surface-scrim);
  z-index: var(--z-modal);
  display: flex;
  align-items: flex-end;
}

.qp-mb__sheet-panel {
  width: 100%;
  padding: var(--space-2xs);
  background: var(--surface-base);
  border-top: 1px solid var(--hairline);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qp-mb__sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--space-xs);
  margin-bottom: var(--space-2xs);
  border-bottom: 1px solid var(--hairline);
}

.qp-mb__sheet-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  color: var(--ink-muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color var(--dur-xs) var(--ease-out-quart),
              background-color var(--dur-xs) var(--ease-out-quart);
}

.qp-mb__sheet-close:hover {
  color: var(--ink);
  background: var(--surface-sunken);
}

.qp-mb__sheet-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  padding: 12px var(--space-sm);
  color: var(--ink);
  background: transparent;
  border-radius: var(--radius-sm);
  text-decoration: none;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  transition:
    background-color var(--dur-xs) var(--ease-out-quart),
    padding-inline-start var(--dur-xs) var(--ease-out-quart);
}

.qp-mb__sheet-link:hover {
  background: var(--surface-sunken);
}

.qp-mb__sheet-link-label { min-width: 0; }

.qp-mb__sheet-link-hint {
  font-family: var(--font-mono);
  font-size: var(--text-2xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--ink-faint);
}

.qp-mb-sheet-enter-active,
.qp-mb-sheet-leave-active { transition: opacity var(--dur-md) var(--ease-out-quart); }
.qp-mb-sheet-enter-from,
.qp-mb-sheet-leave-to { opacity: 0; }

.qp-mb-sheet-enter-active .qp-mb__sheet-panel,
.qp-mb-sheet-leave-active .qp-mb__sheet-panel {
  transition: transform var(--dur-md) var(--ease-out-expo);
}
.qp-mb-sheet-enter-from .qp-mb__sheet-panel,
.qp-mb-sheet-leave-to .qp-mb__sheet-panel {
  transform: translateY(100%);
}

@media (max-width: 960px) {
  .qp-mb { display: flex; }
}

@media (prefers-reduced-motion: reduce) {
  .qp-mb__tab,
  .qp-mb-sheet-enter-active,
  .qp-mb-sheet-leave-active { transition: none; }
  .qp-mb__sheet-panel { transition: none; }
}
</style>
